import React from 'react';
import i18next from 'i18next';
import queryString from 'query-string';
import { withTranslation } from 'react-i18next';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import moment from 'moment';

import { withTheme } from '@material-ui/core/styles';

import { getFormat, twoDigits, makeGraph } from 'lib/commonLib';
import { analysisDayStatisticsGraph } from 'lib/api/adex';

import CurrentStatus from 'components/atoms/CurrentStatus';
import OutlinedButton from 'components/atoms/OutlinedButton';
import ContentsWrap from 'components/molecules/ContentsWrap';
import InfoTitleArea from 'components/molecules/MbTable/InfoTitleArea';
import ChartButtonWrap from 'components/molecules/ChartButtonWrap';
import SwitchingArea from 'components/organisms/SwitchingArea';
import MultiplexChart from 'components/organisms/Chart/MultiplexChart';

const graphList = [
  {
    title: i18next.t('지출금액'),
    data: { bar: ['PAR_EPRS_CNT', 'PAR_EPRS_CNT'], line: ['PAR_EPRS_CNT'] },
    yAxisData: {
      left: { ADVER_CNT: true },
      right: { PAR_EPRS_CNT: true },
    },
    referenceLine: {
      PAR_EPRS_CNT: {
        type: 'average',
        label: i18next.t('지출금액'),
        visibleValue: true,
      },
    },
  },
];

class StatisticsGraph extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      graphData: [],
      infoMessage: '',
      loading: false,
      error: false,
    };
  }

  getData = () => {
    const { params, locale, t, location } = this.props;
    const { uri } = queryString.parse(location.search);

    const { sDate, eDate } = params;
    const format = getFormat(locale);
    this.setState({
      ...this.state,
      loading: true,
      error: false,
      infoMessage: t('까지의 통계 그래프입니다.', {
        date: t('term', {
          startDate: moment(sDate).format(format),
          endDate: moment(eDate).format(format),
        }),
      }),
    });
    analysisDayStatisticsGraph({ ...params, uri })
      .then((response) => {
        const { data } = response.data;
        this.setState({
          ...this.state,
          primitiveData: data,
          loading: false,
          error: false,
        });

        this.handleChangeGraph(graphList[0], 0)();
      })
      .catch((error) => {
        this.setState({
          ...this.state,
          loading: false,
          error: true,
        });
      });
  };

  componentDidMount() {
    this.getData();
  }

  componentDidUpdate(prevProps) {
    const { params: prevParams } = prevProps;
    const { params } = this.props;

    if (JSON.stringify(prevParams) !== JSON.stringify(params)) {
      this.getData();
    }
  }

  /**
   * 새로고침
   */
  handleRefesh = () => {
    this.getData();
  };

  // 월 일 or 일 반환
  makeLabel = (onlyDay) => (originalDate) => {
    const { t, theme } = this.props;
    const dateObj = moment(originalDate, 'YYYYMMDD');
    const month = twoDigits(dateObj.month() + 1);
    const date = dateObj.date();
    const day = dateObj.day();
    let color = '';

    if (day === 0) {
      color = theme.palette.common.red;
    } else if (day === 6) {
      color = theme.palette.common.blue;
    }

    return onlyDay ? (
      `${twoDigits(date)}${t('일')}`
    ) : (
      <span style={{ color }}>{`${t(month + '월')} ${twoDigits(date)}${t(
        '일'
      )}`}</span>
    );
  };

  xAxisTick = (tickProps) => {
    const { theme } = this.props;
    const { x, y, payload } = tickProps;
    const { value } = payload;
    const dateObj = moment(value, 'YYYYMMDD');
    const day = dateObj.day();

    const dayStr = this.makeLabel(true)(value);
    let fill = '';
    //Sunday as 0 and Saturday as 6.
    if (day === 0) {
      fill = theme.palette.common.red;
    } else if (day === 6) {
      fill = theme.palette.common.blue;
    }

    return (
      <text x={x} y={y + 10} fill={fill} textAnchor="middle">
        {dayStr}
      </text>
    );
  };

  /**
   * 팝업
   */
  handleCreatePop = (uri) => {
    console.log('uri', uri);

    window.open(uri, 'pop');
    return false;
  };

  /**
   * 버튼 클릭하여 그래프 데이터 변경
   * graphData json, index
   */
  handleChangeGraph =
    (
      {
        title,
        data,
        yAxisData,
        customizeData: customize,
        isPercent = false,
        referenceLine,
      },
      index
    ) =>
    (e) => {
      const { primitiveData } = this.state;

      if (index === 13) {
        let showWindowUrl = '/report/daily/topMedia?isPop=1';
        this.handleCreatePop(showWindowUrl);
      } else if (index === 15) {
        let showWindowUrl = '/report/daily/longTime?isPop=1';
        this.handleCreatePop(showWindowUrl);
      }

      if (typeof data !== 'undefined') {
        const { graphData, dataKeys, customizeData, keyLabel, referenceData } =
          makeGraph({ data, customize, primitiveData, referenceLine });

        const result = (
          <MultiplexChart
            xAxisTick={this.xAxisTick}
            makeLabel={this.makeLabel(false)}
            data={graphData}
            dataKeys={dataKeys}
            customizeData={customizeData}
            keyLabel={keyLabel}
            stackId={null}
            yAxisData={yAxisData}
            legend
            title={title}
            isPercent={isPercent}
            key={title}
            referenceLine={referenceData}
          />
        );

        this.setState({
          ...this.state,
          curGraphComponent: result,
          selectedIdx: index,
        });
      }
    };

  render() {
    const { handleRefesh, handleChangeGraph } = this;
    const { t } = this.props;
    const {
      infoMessage,
      curGraphComponent,
      selectedIdx,
      primitiveData,
      loading,
      error,
    } = this.state;

    return (
      <SwitchingArea
        title={
          <>
            <InfoTitleArea
              title={t('일자별 데이터 그래프')}
              tooltipTitle={infoMessage}
              iconType="fainfoCircle"
              iconColor="graph"
            />
          </>
        }
        handleRefesh={handleRefesh}
      >
        {loading || error ? (
          <CurrentStatus type={loading ? 'loading' : error && 'error'} />
        ) : (
          <>
            <ChartButtonWrap>
              {graphList.map((item, index) => {
                return (
                  <OutlinedButton
                    key={item.title}
                    onClick={handleChangeGraph(item, index)}
                    selected={selectedIdx === index}
                    disabled={!Boolean(primitiveData)}
                  >
                    {item.title}
                  </OutlinedButton>
                );
              })}
            </ChartButtonWrap>
            <ContentsWrap contentsCount={1}>{curGraphComponent}</ContentsWrap>
          </>
        )}
      </SwitchingArea>
    );
  }
}

export default withRouter(
  withTranslation()(
    connect(
      ({ locale }) => ({
        locale: locale.locale,
      }),
      null
    )(withTheme(StatisticsGraph))
  )
);
